import { useState, type FormEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Dialog } from '../ui/Dialog/Dialog';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Textarea } from '../atoms/Textarea/Textarea';
import { localToday } from '../livestock/livestockDeceased';
import { INTERVENTION_KINDS, createIntervention, type Intervention, type InterventionKind } from '../../services/incidentService';
import './IncidentDialogs.css';

export interface InterventionDialogProps {
  aquariumId: string;
  isOpen: boolean;
  onClose: () => void;
  onCreated: (intervention: Intervention) => void;
}

/** "I changed X" (Reef Guard #1824): one intervention log entry, optionally with an end date. */
export function InterventionDialog({ aquariumId, isOpen, onClose, onCreated }: InterventionDialogProps) {
  const { t } = useTranslation();
  const [today] = useState(localToday);
  const [kind, setKind] = useState<InterventionKind>('water_change');
  const [date, setDate] = useState(today);
  const [endDate, setEndDate] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<{ date?: string; endDate?: string }>({});
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const next: { date?: string; endDate?: string } = {};
    if (!date || date > today) next.date = t('incidents.intervention.dateError');
    if (endDate && (endDate < date || endDate > today)) next.endDate = t('incidents.intervention.endDateError');
    setErrors(next);
    if (next.date || next.endDate) return;
    setSaving(true);
    setSaveError(false);
    try {
      onCreated(
        await createIntervention(aquariumId, {
          kind,
          note: note.trim() || null,
          occurred_at: new Date(`${date}T12:00:00`).toISOString(),
          ended_at: endDate ? new Date(`${endDate}T12:00:00`).toISOString() : null,
        }),
      );
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title={t('incidents.intervention.title')} size="sm" data-testid="intervention-dialog">
      <form onSubmit={submit} noValidate>
        <Stack gap="4">
          <Text>{t('incidents.intervention.intro')}</Text>
          {saveError && <Alert>{t('incidents.intervention.saveError')}</Alert>}
          <Select
            id="intervention-kind"
            label={t('incidents.intervention.kind')}
            value={kind}
            options={INTERVENTION_KINDS.map((k) => ({ value: k, label: String(t(`incidents.interventionKinds.${k}`)) }))}
            onChange={(e) => setKind(e.target.value as InterventionKind)}
            disabled={saving}
          />
          <Input
            id="intervention-date"
            type="date"
            label={t('incidents.intervention.date')}
            value={date}
            max={today}
            onChange={(e) => {
              setDate(e.target.value);
              setErrors({});
            }}
            error={errors.date}
            required
            disabled={saving}
          />
          <Input
            id="intervention-end-date"
            type="date"
            label={t('incidents.intervention.endDate')}
            helperText={t('incidents.intervention.endDateHelp')}
            value={endDate}
            min={date || undefined}
            max={today}
            onChange={(e) => {
              setEndDate(e.target.value);
              setErrors({});
            }}
            error={errors.endDate}
            disabled={saving}
          />
          <Textarea
            id="intervention-note"
            label={t('incidents.intervention.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('incidents.intervention.notePlaceholder')}
            rows={2}
            maxLength={500}
            disabled={saving}
          />
          <div className="incident-dialog__actions">
            <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
              {t('incidents.cancel')}
            </Button>
            <Button type="submit" isLoading={saving}>
              {t('incidents.intervention.submit')}
            </Button>
          </div>
        </Stack>
      </form>
    </Dialog>
  );
}
